import { useEffect, useState } from "react";
import { formatJalaliDate } from "@/lib/format";

import { getCommodities } from "../api/traceabilityApi";
import { useTraceabilityDashboard } from "../hooks/useTraceabilityDashboard";
import { useTraceabilityGraph } from "../hooks/useTraceabilityGraph";
import useAlertFilters from "../hooks/useAlertFilters";
import { fa } from "../i18n/fa";
import CommodityTabs from "../components/CommodityTabs";
import KpiStrip from "../components/KpiStrip";
import RetentionChart from "../components/RetentionChart";
import PriceLadderChart from "../components/PriceLadderChart";
import HoardingChart from "../components/HoardingChart";
import RetailTrendChart from "../components/RetailTrendChart";
import AlertsPanel from "../components/AlertsPanel";
import AlertsByRuleChart from "../components/AlertsByRuleChart";
import ChainExplorer from "../components/ChainExplorer";
import SourcesPanel from "../components/SourcesPanel";
import "../traceability.css";

export default function TraceabilityDashboardPage() {
  const [items, setItems] = useState([]);
  const [commodity, setCommodity] = useState("red_meat");
  const [selected, setSelected] = useState(null);
  const { data, loading, error } = useTraceabilityDashboard(commodity);
  const { filters, updateFilters, clearFilters } = useAlertFilters();

  useEffect(() => {
    getCommodities().then(setItems).catch(() => {});
  }, []);

  // The inspected chain follows the user's choice; until they pick one, the
  // first chain carrying a critical alert is the useful default.
  const chain =
    data?.chains.find((c) => c.id === selected) ??
    data?.chains.find((c) => c.flags.some((f) => f.severity === "critical")) ??
    data?.chains[0];
  const { graph, loading: graphLoading, error: graphError } = useTraceabilityGraph(
    commodity,
    chain?.id,
  );
  const selectSeverity = (severity) => updateFilters({ severities: [severity] });
  const selectRule = (rule) => updateFilters({ rule });

  return (
    <main className="trace" dir="rtl">
      <div className="tr-top">
        <div className="tr-top-text">
          <p className="tr-eyebrow">{fa.page.eyebrow}</p>
          <h1>{fa.page.title}</h1>
          <div className="tr-sub">
            {data ? fa.page.asOf(formatJalaliDate(data.as_of)) : fa.page.loading}
          </div>
        </div>
        <CommodityTabs items={items} value={commodity} onChange={setCommodity} />
      </div>

      {error && <div className="tr-card tr-error">{fa.page.error}</div>}
      {loading && !data && <div className="tr-card tr-loading">{fa.page.loading}</div>}

      {data && (
        <>
          <KpiStrip
            kpis={data.kpis}
            sources={data.sources}
            onSeveritySelect={selectSeverity}
          />
          <div className="tr-grid tr-retention-grid">
            <RetentionChart
              retention={data.retention}
              chain={chain}
              tolerancePct={graph?.alert_rules?.quantity_retention_tolerance_pct}
            />
            <PriceLadderChart ladder={data.price_ladder} chain={chain} units={data.units} />
          </div>
          <div className="tr-grid tr-chain-grid">
            <ChainExplorer
              chains={data.chains}
              chain={chain}
              onSelect={setSelected}
              units={data.units}
              graph={graph}
              graphLoading={graphLoading}
              graphError={graphError}
            />
            <AlertsPanel
              alerts={data.alerts}
              chains={data.chains}
              commodity={data.commodity}
              onSelect={setSelected}
              units={data.units}
              filters={filters}
              onFilterChange={updateFilters}
              onClearFilters={clearFilters}
            />
          </div>
          <div className="tr-grid">
            <HoardingChart hoarding={data.hoarding} />
            <RetailTrendChart trend={data.trend} units={data.units} />
          </div>
          <div className="tr-grid">
            <AlertsByRuleChart alerts={data.alerts} onRuleSelect={selectRule} />
            <SourcesPanel sources={data.sources} />
          </div>
        </>
      )}
    </main>
  );
}
